// /src/constants.js
// Dynamic pricing system - all prices based on $10 USD with real-time exchange rates

// Backend URL — switch the fallback between local and production as needed
// LOCAL TESTING:  'http://localhost:5000'
// PRODUCTION:     'https://residency-companion-backend.vercel.app'
export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://residency-companion-backend.vercel.app';

// Base price in USD
export const BASE_PRICE_USD = 10;

// Fallback exchange rates (used if API fails)
const FALLBACK_EXCHANGE_RATES = {
  "USD": 1.00,        // Base currency
  "INR": 83.50,       // Indian Rupee
  "GBP": 0.79,        // British Pound
  "EUR": 0.92,        // Euro
  "CAD": 1.37,        // Canadian Dollar
  "AUD": 1.52,        // Australian Dollar
  "SGD": 1.34,        // Singapore Dollar
  "MYR": 4.48,        // Malaysian Ringgit
  "THB": 34.20,       // Thai Baht
  "PHP": 56.50,       // Philippine Peso
  "IDR": 15800,       // Indonesian Rupiah
  "VND": 24500,       // Vietnamese Dong
  "JPY": 149.00,      // Japanese Yen
  "KRW": 1330,        // South Korean Won
  "PKR": 278.00,      // Pakistani Rupee
  "BDT": 110.00,      // Bangladeshi Taka
  "LKR": 307.00,      // Sri Lankan Rupee
  "NPR": 133.60,      // Nepalese Rupee
  "SAR": 3.75,        // Saudi Riyal
  "AED": 3.67,        // UAE Dirham
  "QAR": 3.64,        // Qatari Riyal
  "KWD": 0.31,        // Kuwaiti Dinar
  "BHD": 0.38,        // Bahraini Dinar
  "OMR": 0.38,        // Omani Rial
  "JOD": 0.71,        // Jordanian Dinar
  "ZAR": 18.20,       // South African Rand
  "NGN": 830.00,      // Nigerian Naira
  "KES": 129.00,      // Kenyan Shilling
  "EGP": 49.50,       // Egyptian Pound
  "BRL": 5.95,        // Brazilian Real
  "MXN": 20.15,       // Mexican Peso
  "ARS": 850.00,      // Argentine Peso
  "CLP": 950.00,      // Chilean Peso
  "COP": 4200.00,     // Colombian Peso
  "RUB": 92.00,       // Russian Ruble
  "TRY": 32.50,       // Turkish Lira
  "CNY": 7.25,        // Chinese Yuan
  "HKD": 7.80,        // Hong Kong Dollar
  "TWD": 32.00,       // Taiwan Dollar
  "CHF": 0.88,        // Swiss Franc
  "SEK": 10.85,       // Swedish Krona
  "NOK": 11.20,       // Norwegian Krone
  "DKK": 6.85,        // Danish Krone
  "PLN": 4.05,        // Polish Zloty
  "CZK": 23.50,       // Czech Koruna
  "HUF": 380.00,      // Hungarian Forint
  "RON": 4.58,        // Romanian Leu
  "BGN": 1.80,        // Bulgarian Lev
  "HRK": 6.93,        // Croatian Kuna
  "RSD": 108.00,      // Serbian Dinar
  "UAH": 41.50,       // Ukrainian Hryvnia
  "ILS": 3.72,        // Israeli Shekel
  "MAD": 10.15,       // Moroccan Dirham
  "TND": 3.15,        // Tunisian Dinar
  "DZD": 134.50,      // Algerian Dinar
  "ETB": 122.00,      // Ethiopian Birr
  "GHS": 15.80,       // Ghanaian Cedi
  "UGX": 3720,        // Ugandan Shilling
  "TZS": 2510,        // Tanzanian Shilling
  "RWF": 1320,        // Rwandan Franc
  "XOF": 603.00,      // West African CFA Franc
  "XAF": 603.00,      // Central African CFA Franc
};

// Live exchange rates storage
let LIVE_EXCHANGE_RATES = { ...FALLBACK_EXCHANGE_RATES };
let LAST_UPDATED = null;
let UPDATE_IN_PROGRESS = false;

// Cache duration (6 hours)
const CACHE_DURATION = 6 * 60 * 60 * 1000; // 6 hours in milliseconds

// Country to currency mapping
export const COUNTRY_CURRENCY_MAP = {
  // Major markets
  "United States": "USD",
  "India": "INR",
  "United Kingdom": "GBP",
  "Canada": "CAD",
  "Australia": "AUD",
  
  // European Union (EUR)
  "Germany": "EUR",
  "France": "EUR",
  "Spain": "EUR",
  "Italy": "EUR",
  "Netherlands": "EUR",
  "Austria": "EUR",
  "Belgium": "EUR",
  "Finland": "EUR",
  "Ireland": "EUR",
  "Portugal": "EUR",
  "Greece": "EUR",
  "Luxembourg": "EUR",
  "Slovenia": "EUR",
  "Slovakia": "EUR",
  "Estonia": "EUR",
  "Latvia": "EUR",
  "Lithuania": "EUR",
  "Malta": "EUR",
  "Cyprus": "EUR",
  
  // Asian markets
  "Japan": "JPY",
  "Singapore": "SGD",
  "Malaysia": "MYR",
  "Thailand": "THB",
  "Philippines": "PHP",
  "Indonesia": "IDR",
  "Vietnam": "VND",
  "South Korea": "KRW",
  "China": "CNY",
  "Hong Kong": "HKD",
  "Taiwan": "TWD",
  
  // South Asian countries
  "Pakistan": "PKR",
  "Bangladesh": "BDT",
  "Sri Lanka": "LKR",
  "Nepal": "NPR",
  
  // Middle East
  "Saudi Arabia": "SAR",
  "United Arab Emirates": "AED",
  "Qatar": "QAR",
  "Kuwait": "KWD",
  "Bahrain": "BHD",
  "Oman": "OMR",
  "Jordan": "JOD",
  "Israel": "ILS",
  
  // African markets
  "South Africa": "ZAR",
  "Nigeria": "NGN",
  "Kenya": "KES",
  "Egypt": "EGP",
  "Ghana": "GHS",
  "Uganda": "UGX",
  "Tanzania": "TZS",
  "Rwanda": "RWF",
  "Ethiopia": "ETB",
  "Morocco": "MAD",
  "Tunisia": "TND",
  "Algeria": "DZD",
  
  // Latin American markets
  "Brazil": "BRL",
  "Mexico": "MXN",
  "Argentina": "ARS",
  "Chile": "CLP",
  "Colombia": "COP",
  
  // Other European countries
  "Switzerland": "CHF",
  "Sweden": "SEK",
  "Norway": "NOK",
  "Denmark": "DKK",
  "Poland": "PLN",
  "Czech Republic": "CZK",
  "Hungary": "HUF",
  "Romania": "RON",
  "Bulgaria": "BGN",
  "Croatia": "HRK",
  "Serbia": "RSD",
  "Ukraine": "UAH",
  
  // Other markets
  "Russia": "RUB",
  "Turkey": "TRY",
};

// Supported Razorpay currencies
export const RAZORPAY_SUPPORTED_CURRENCIES = [
  "INR", "USD", "EUR", "GBP", "AUD", "CAD", "SGD", "AED", "MYR", "THB", 
  "PHP", "VND", "IDR", "HKD", "JPY", "KRW", "LKR", "NPR", "BDT", "PKR",
  "SAR", "QAR", "KWD", "BHD", "OMR", "JOD", "EGP", "ZAR", "KES", "UGX",
  "TZS", "GHS", "NGN", "XOF", "XAF", "MAD", "TND", "DZD", "ETB", "RWF"
];

// Currencies that don't use decimal places
export const NO_DECIMAL_CURRENCIES = ["JPY", "KRW", "VND", "IDR", "CLP", "PYG", "UGX", "RWF", "XOF", "XAF", "HUF"];

// Currency formatting options
export const CURRENCY_FORMATS = {
  "INR": { decimals: 2, symbol: "₹" },
  "USD": { decimals: 2, symbol: "$" },
  "EUR": { decimals: 2, symbol: "€" },
  "GBP": { decimals: 2, symbol: "£" },
  "JPY": { decimals: 0, symbol: "¥" },
  "KRW": { decimals: 0, symbol: "₩" },
  "VND": { decimals: 0, symbol: "₫" },
  "IDR": { decimals: 0, symbol: "Rp" },
  "CAD": { decimals: 2, symbol: "C$" },
  "AUD": { decimals: 2, symbol: "A$" },
  "SGD": { decimals: 2, symbol: "S$" },
  "MYR": { decimals: 2, symbol: "RM" },
  "THB": { decimals: 2, symbol: "฿" },
  "PHP": { decimals: 2, symbol: "₱" },
  "PKR": { decimals: 2, symbol: "₨" },
  "BDT": { decimals: 2, symbol: "৳" },
  "LKR": { decimals: 2, symbol: "Rs" },
  "NPR": { decimals: 2, symbol: "Rs" },
  "SAR": { decimals: 2, symbol: "﷼" },
  "AED": { decimals: 2, symbol: "د.إ" },
  "QAR": { decimals: 2, symbol: "﷼" },
  "KWD": { decimals: 3, symbol: "د.ك" },
  "BHD": { decimals: 3, symbol: ".د.ب" },
  "OMR": { decimals: 3, symbol: "﷼" },
  "JOD": { decimals: 3, symbol: "د.ا" },
  "ZAR": { decimals: 2, symbol: "R" },
  "NGN": { decimals: 2, symbol: "₦" },
  "KES": { decimals: 2, symbol: "KSh" },
  "EGP": { decimals: 2, symbol: "£" },
  "BRL": { decimals: 2, symbol: "R$" },
  "MXN": { decimals: 2, symbol: "$" },
  "ARS": { decimals: 2, symbol: "$" },
  "CLP": { decimals: 0, symbol: "$" },
  "COP": { decimals: 2, symbol: "$" },
  "RUB": { decimals: 2, symbol: "₽" },
  "TRY": { decimals: 2, symbol: "₺" },
  "CNY": { decimals: 2, symbol: "¥" },
  "HKD": { decimals: 2, symbol: "HK$" },
  "TWD": { decimals: 2, symbol: "NT$" },
  "CHF": { decimals: 2, symbol: "Fr" },
  "SEK": { decimals: 2, symbol: "kr" },
  "NOK": { decimals: 2, symbol: "kr" },
  "DKK": { decimals: 2, symbol: "kr" },
  "PLN": { decimals: 2, symbol: "zł" },
  "CZK": { decimals: 2, symbol: "Kč" },
  "HUF": { decimals: 0, symbol: "Ft" },
  "RON": { decimals: 2, symbol: "lei" },
  "BGN": { decimals: 2, symbol: "лв" },
  "HRK": { decimals: 2, symbol: "kn" },
  "RSD": { decimals: 2, symbol: "дин" },
  "UAH": { decimals: 2, symbol: "₴" },
  "ILS": { decimals: 2, symbol: "₪" },
  "MAD": { decimals: 2, symbol: "د.م." },
  "TND": { decimals: 3, symbol: "د.ت" },
  "DZD": { decimals: 2, symbol: "د.ج" },
  "ETB": { decimals: 2, symbol: "Br" },
  "GHS": { decimals: 2, symbol: "₵" },
  "UGX": { decimals: 0, symbol: "USh" },
  "TZS": { decimals: 0, symbol: "TSh" },
  "RWF": { decimals: 0, symbol: "FRw" },
  "XOF": { decimals: 0, symbol: "CFA" },
  "XAF": { decimals: 0, symbol: "FCFA" }
};

// Multiple API endpoints for redundancy
const EXCHANGE_RATE_APIS = [
  {
    name: 'ExchangeRate-API',
    url: 'https://api.exchangerate-api.com/v4/latest/USD',
    parseResponse: (data) => data.rates
  },
  {
    name: 'Fixer.io',
    url: 'https://api.fixer.io/latest?base=USD',
    parseResponse: (data) => data.rates
  },
  {
    name: 'CurrencyAPI',
    url: 'https://api.currencyapi.com/v3/latest?apikey=YOUR_API_KEY&base_currency=USD',
    parseResponse: (data) => {
      const rates = {};
      Object.keys(data.data).forEach(currency => {
        rates[currency] = data.data[currency].value;
      });
      return rates;
    }
  }
];

// Function to fetch live exchange rates
export const updateExchangeRates = async (forceUpdate = false) => {
  // Check if update is needed
  if (!forceUpdate && LAST_UPDATED && (Date.now() - LAST_UPDATED < CACHE_DURATION)) {
    console.log('Exchange rates are still fresh, using cached rates');
    return true;
  }

  if (UPDATE_IN_PROGRESS) {
    console.log('Exchange rate update already in progress');
    return false;
  }

  UPDATE_IN_PROGRESS = true;

  console.log('🔄 Updating exchange rates...');
  
  // Try each API endpoint until one succeeds
  for (const api of EXCHANGE_RATE_APIS) {
    try {
      console.log(`📡 Trying ${api.name}...`);
      
      const response = await fetch(api.url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      const rates = api.parseResponse(data);
      
      if (!rates || typeof rates !== 'object') {
        throw new Error('Invalid response format');
      }
      
      // Update rates for currencies we support
      const updatedRates = { USD: 1.00 }; // Base currency
      let updateCount = 0;
      
      Object.keys(FALLBACK_EXCHANGE_RATES).forEach(currency => {
        if (currency === 'USD') return;
        
        if (rates[currency] && typeof rates[currency] === 'number' && rates[currency] > 0) {
          updatedRates[currency] = rates[currency];
          updateCount++;
        } else {
          // Keep fallback rate if API doesn't have this currency
          updatedRates[currency] = FALLBACK_EXCHANGE_RATES[currency];
        }
      });
      
      if (updateCount > 0) {
        LIVE_EXCHANGE_RATES = updatedRates;
        LAST_UPDATED = Date.now();
        
        console.log(`✅ Exchange rates updated successfully via ${api.name}`);
        console.log(`📊 Updated ${updateCount} currencies`);
        console.log(`⏰ Next update in ${Math.round(CACHE_DURATION / (1000 * 60 * 60))} hours`);
        
        UPDATE_IN_PROGRESS = false;
        return true;
      } else {
        throw new Error('No valid exchange rates received');
      }
      
    } catch (error) {
      console.warn(`⚠️ ${api.name} failed:`, error.message);
      continue;
    }
  }
  
  console.error('❌ All exchange rate APIs failed, using fallback rates');
  UPDATE_IN_PROGRESS = false;
  return false;
};

// Helper function to get current exchange rates
export const getCurrentExchangeRates = () => {
  return { ...LIVE_EXCHANGE_RATES };
};

// Helper function to get last update time
export const getLastUpdateTime = () => {
  return LAST_UPDATED;
};

// Helper function to convert USD to local currency with live rates - FIXED VERSION
export const convertUSDToLocalCurrency = (usdAmount, targetCurrency) => {
  // Validate inputs
  if (!usdAmount || isNaN(Number(usdAmount))) {
    console.warn('convertUSDToLocalCurrency: Invalid USD amount:', usdAmount);
    usdAmount = BASE_PRICE_USD; // Use base price as fallback
  }

  if (!targetCurrency || typeof targetCurrency !== 'string') {
    console.warn('convertUSDToLocalCurrency: Invalid target currency:', targetCurrency);
    targetCurrency = "USD";
  }

  const rate = LIVE_EXCHANGE_RATES[targetCurrency];
  if (!rate || typeof rate !== 'number' || rate <= 0) {
    console.warn(`Exchange rate not found or invalid for ${targetCurrency}, using USD`);
    return { 
      amount: Number(usdAmount), 
      currency: "USD",
      rate: 1,
      lastUpdated: LAST_UPDATED 
    };
  }
  
  const convertedAmount = Number(usdAmount) * rate;
  
  // Round appropriately based on currency
  let finalAmount;
  if (NO_DECIMAL_CURRENCIES.includes(targetCurrency)) {
    finalAmount = Math.round(convertedAmount);
  } else {
    // Round to appropriate decimal places
    const decimals = CURRENCY_FORMATS[targetCurrency]?.decimals || 2;
    finalAmount = Math.round(convertedAmount * Math.pow(10, decimals)) / Math.pow(10, decimals);
  }
  
  return {
    amount: finalAmount,
    currency: targetCurrency,
    rate: rate,
    lastUpdated: LAST_UPDATED
  };
};

// Helper function to get pricing for a country (auto-converts from $10 USD with live rates)
export const getPricingForCountry = async (country) => {
  // Update exchange rates if needed (non-blocking)
  updateExchangeRates().catch(err => 
    console.warn('Exchange rate update failed, using cached/fallback rates:', err.message)
  );
  
  // Get currency for the country
  const currency = COUNTRY_CURRENCY_MAP[country] || "USD";
  
  // Convert $10 USD to local currency using live rates
  const converted = convertUSDToLocalCurrency(BASE_PRICE_USD, currency);
  
  return converted;
};

// Synchronous version that doesn't trigger updates (for immediate use) - FIXED VERSION
export const getPricingForCountrySync = (country) => {
  // Handle invalid country input
  if (!country || typeof country !== 'string') {
    console.warn('getPricingForCountrySync: Invalid country provided:', country);
    return {
      amount: BASE_PRICE_USD,
      currency: "USD",
      rate: 1,
      lastUpdated: LAST_UPDATED
    };
  }

  const currency = COUNTRY_CURRENCY_MAP[country] || "USD";
  
  try {
    const converted = convertUSDToLocalCurrency(BASE_PRICE_USD, currency);
    
    // Ensure the converted object has all required properties
    return {
      amount: converted.amount || BASE_PRICE_USD,
      currency: converted.currency || "USD",
      rate: converted.rate || 1,
      lastUpdated: converted.lastUpdated || LAST_UPDATED
    };
  } catch (error) {
    console.error('getPricingForCountrySync: Error converting currency:', error);
    
    // Return safe fallback
    return {
      amount: BASE_PRICE_USD,
      currency: "USD",
      rate: 1,
      lastUpdated: LAST_UPDATED
    };
  }
};

// Helper function to check if currency is supported by Razorpay
export const isCurrencySupported = (currency) => {
  return RAZORPAY_SUPPORTED_CURRENCIES.includes(currency);
};

// Helper function to format currency amount - FIXED VERSION
export const formatCurrency = (amount, currency = "USD") => {
  // Handle undefined, null, or invalid amount
  if (amount === undefined || amount === null || isNaN(Number(amount))) {
    console.warn('formatCurrency: Invalid amount provided:', amount, 'currency:', currency);
    // Return a default formatted value based on currency
    const fallbackFormat = CURRENCY_FORMATS[currency] || { decimals: 2, symbol: "$" };
    return `${fallbackFormat.symbol}0${NO_DECIMAL_CURRENCIES.includes(currency) ? '' : '.00'}`;
  }

  // Handle undefined or invalid currency
  if (!currency || typeof currency !== 'string') {
    console.warn('formatCurrency: Invalid currency provided:', currency, 'amount:', amount);
    currency = "USD"; // Default to USD
  }

  // Ensure amount is a number
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
  
  if (isNaN(numAmount)) {
    console.warn('formatCurrency: Could not parse amount to number:', amount);
    const fallbackFormat = CURRENCY_FORMATS[currency] || { decimals: 2, symbol: "$" };
    return `${fallbackFormat.symbol}0${NO_DECIMAL_CURRENCIES.includes(currency) ? '' : '.00'}`;
  }

  try {
    // Get format for this currency, with fallback
    const format = CURRENCY_FORMATS[currency] || { decimals: 2, symbol: "$" };
    
    // Special handling for currencies without decimals
    const decimals = NO_DECIMAL_CURRENCIES.includes(currency) ? 0 : format.decimals;
    
    // Use toLocaleString with proper error handling
    const formattedNumber = numAmount.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
    
    return `${format.symbol}${formattedNumber}`;
    
  } catch (error) {
    console.error('formatCurrency: Error formatting currency:', error);
    
    // Fallback formatting
    const format = CURRENCY_FORMATS[currency] || { decimals: 2, symbol: "$" };
    const decimals = NO_DECIMAL_CURRENCIES.includes(currency) ? 0 : format.decimals;
    
    return `${format.symbol}${numAmount.toFixed(decimals)}`;
  }
};

// Helper function to get amount in smallest currency unit for payment processors
export const getPaymentAmount = (amount, currency) => {
  // Handle invalid amount
  if (!amount || isNaN(Number(amount))) {
    console.warn('getPaymentAmount: Invalid amount provided:', amount);
    return 0;
  }

  // Handle invalid currency
  if (!currency || typeof currency !== 'string') {
    console.warn('getPaymentAmount: Invalid currency provided:', currency);
    currency = "USD";
  }

  const numAmount = Number(amount);

  if (NO_DECIMAL_CURRENCIES.includes(currency)) {
    return Math.round(numAmount); // No conversion needed for non-decimal currencies
  } else {
    const decimals = CURRENCY_FORMATS[currency]?.decimals || 2;
    return Math.round(numAmount * Math.pow(10, decimals)); // Convert to cents/paise/etc.
  }
};

// Function to initialize exchange rates on app start
export const initializeExchangeRates = async () => {
  console.log('🚀 Initializing exchange rates...');
  
  // Try to load cached rates from localStorage
  try {
    const cached = localStorage.getItem('exchangeRates');
    const cacheTime = localStorage.getItem('exchangeRatesTime');
    
    if (cached && cacheTime) {
      const parsedRates = JSON.parse(cached);
      const parsedTime = parseInt(cacheTime);
      
      // Use cached rates if they're less than 6 hours old
      if (Date.now() - parsedTime < CACHE_DURATION) {
        LIVE_EXCHANGE_RATES = parsedRates;
        LAST_UPDATED = parsedTime;
        console.log('📱 Using cached exchange rates');
        return true;
      }
    }
  } catch (error) {
    console.warn('Failed to load cached exchange rates:', error);
  }
  
  // Fetch fresh rates
  const success = await updateExchangeRates(true);
  
  // Cache the rates if successful
  if (success) {
    try {
      localStorage.setItem('exchangeRates', JSON.stringify(LIVE_EXCHANGE_RATES));
      localStorage.setItem('exchangeRatesTime', LAST_UPDATED.toString());
    } catch (error) {
      console.warn('Failed to cache exchange rates:', error);
    }
  }
  
  return success;
};
// ===== PER-SPECIALTY PRICING FUNCTIONS =====

// Function to get pricing info for a specific specialty
export const getSpecialtyPricingInfo = (specialty, country = "India") => {
  try {
    const basePricing = getPricingForCountrySync(country);
    
    return {
      ...basePricing,
      specialty: specialty,
      description: `Access to ${specialty} programs`,
      isPerSpecialty: true
    };
  } catch (error) {
    console.error('Error getting specialty pricing:', error);
    return {
      amount: BASE_PRICE_USD,
      currency: "USD",
      rate: 1,
      specialty: specialty,
      description: `Access to ${specialty} programs`,
      isPerSpecialty: true,
      lastUpdated: null
    };
  }
};

// Function to check if user has access to a specific specialty
export const checkSpecialtyAccess = (userPaidSpecialties, specialty) => {
  if (!userPaidSpecialties || !Array.isArray(userPaidSpecialties)) {
    return false;
  }
  
  if (!specialty || typeof specialty !== 'string') {
    return false;
  }
  
  const normalizedSpecialty = specialty.toLowerCase().replace(/\s+/g, "_");
  
  return userPaidSpecialties.some(paidSpecialty => {
    if (!paidSpecialty || typeof paidSpecialty !== 'object') {
      return false;
    }
    
    return (
      paidSpecialty.specialty === specialty || 
      paidSpecialty.specialtyNormalized === normalizedSpecialty ||
      paidSpecialty.specialty?.toLowerCase().replace(/\s+/g, "_") === normalizedSpecialty
    );
  });
};

// Function to normalize specialty name for consistent storage
export const normalizeSpecialtyName = (specialty) => {
  if (!specialty || typeof specialty !== 'string') {
    return '';
  }
  return specialty.toLowerCase().replace(/\s+/g, "_");
};

// Function to get all available specialties (you can customize this list)
export const getAvailableSpecialties = () => {
  return [
    "Internal Medicine",
    "Family Medicine", 
    "Neurology",
    "Pediatrics",
    "Psychiatry"
    // Add more specialties as needed
  ];
};
// Auto-update exchange rates every 6 hours
if (typeof window !== 'undefined') {
  // Initialize on load
  initializeExchangeRates();
  
  // Set up periodic updates
  setInterval(() => {
    updateExchangeRates().then(success => {
      if (success) {
        try {
          localStorage.setItem('exchangeRates', JSON.stringify(LIVE_EXCHANGE_RATES));
          localStorage.setItem('exchangeRatesTime', LAST_UPDATED.toString());
        } catch (error) {
          console.warn('Failed to cache updated exchange rates:', error);
        }
      }
    });
  }, CACHE_DURATION);
}

// Export all for easy access
const constantsExports = {
  BASE_PRICE_USD,
  getCurrentExchangeRates,
  getLastUpdateTime,
  COUNTRY_CURRENCY_MAP,
  RAZORPAY_SUPPORTED_CURRENCIES,
  NO_DECIMAL_CURRENCIES,
  CURRENCY_FORMATS,
  convertUSDToLocalCurrency,
  getPricingForCountry,
  getPricingForCountrySync,
  isCurrencySupported,
  formatCurrency,
  getPaymentAmount,
  updateExchangeRates,
  initializeExchangeRates,
  // New per-specialty functions
  getSpecialtyPricingInfo,
  checkSpecialtyAccess,
  normalizeSpecialtyName,
  getAvailableSpecialties
};

export default constantsExports;

